var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import PropTypes from 'prop-types';
import TextCounter from './TextCounter';
import CircleCounter from './CircleCounter';
import FlipCounter from './FlipCounter';
import { resolve, select } from '@cvent/nucleus-dynamic-css';
import { resolveTestId } from '@cvent/nucleus-test-automation';
/**
# Countdown Component
**/
var Countdown = /** @class */ (function (_super) {
    __extends(Countdown, _super);
    function Countdown(props) {
        var _this = _super.call(this, props) || this;
        _this.getCoundownStateFromEndDate = _this.getCoundownStateFromEndDate.bind(_this);
        _this.clearCountdown = _this.clearCountdown.bind(_this);
        _this.startCountdown = _this.startCountdown.bind(_this);
        _this.state = _this.getCoundownStateFromEndDate(props.endDate);
        _this.countdownTimer = null;
        return _this;
    }
    Countdown.prototype.componentDidMount = function () {
        this.startCountdown();
    };
    Countdown.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        if (nextProps.endDate.getTime() !== this.props.endDate.getTime()) {
            this.clearCountdown();
            this.setState(this.getCoundownStateFromEndDate(nextProps.endDate));
            this.startCountdown();
        }
    };
    Countdown.prototype.componentWillUnmount = function () {
        this.clearCountdown();
    };
    Countdown.prototype.getCoundownStateFromEndDate = function (endDate) {
        if (endDate <= new Date()) {
            return {
                daysLeft: 0,
                hoursLeft: 0,
                minutesLeft: 0,
                secondsLeft: 0,
                millisecondsLeft: 0,
                inProgress: false
            };
        }
        var milliseconds = endDate.getTime() - new Date().getTime();
        var days = parseInt(milliseconds / 86400000, 10);
        milliseconds = milliseconds % 86400000;
        var hours = parseInt(milliseconds / 3600000, 10);
        milliseconds = milliseconds % 3600000;
        var minutes = parseInt(milliseconds / 60000, 10);
        milliseconds = milliseconds % 60000;
        var seconds = parseInt(milliseconds / 1000, 10);
        milliseconds = parseInt(milliseconds % 1000, 10);
        return {
            daysLeft: days,
            hoursLeft: hours,
            minutesLeft: minutes,
            secondsLeft: seconds,
            millisecondsLeft: milliseconds,
            inProgress: true
        };
    };
    Countdown.prototype.clearCountdown = function () {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
        }
    };
    Countdown.prototype.startCountdown = function () {
        var _this = this;
        if (this.state.inProgress) {
            setTimeout(function () {
                _this.setState(_this.getCoundownStateFromEndDate(_this.props.endDate));
                _this.countdownTimer = setInterval(function () {
                    if (_this.state.inProgress) {
                        _this.setState(_this.getCoundownStateFromEndDate(_this.props.endDate));
                    }
                    else {
                        clearInterval(_this.countdownTimer);
                        _this.countdownTimer = null;
                    }
                }, 1000);
            }, this.state.millisecondsLeft);
        }
    };
    Countdown.prototype.render = function () {
        var CounterComponent;
        switch (this.props.type) {
            case 'circle':
                CounterComponent = CircleCounter;
                break;
            case 'flip':
                CounterComponent = FlipCounter;
                break;
            case 'text':
                CounterComponent = TextCounter;
                break;
            default:
                throw new Error('Provided countdown type is not supported: ' + this.props.type);
        }
        return React.createElement("ul", __assign({}, resolveTestId(this.props), resolve(this.props, 'countdown')),
            this.props.showDays ? React.createElement(CounterComponent, __assign({}, select(this.props, this.props.type), { label: this.props.daysLabel, digit: this.state.daysLeft, total: 30, type: "days" })) : null,
            this.props.showHours ? React.createElement(CounterComponent, __assign({}, select(this.props, this.props.type), { label: this.props.hoursLabel, digit: this.state.hoursLeft, total: 24, type: "hours" })) : null,
            this.props.showMinutes ? React.createElement(CounterComponent, __assign({}, select(this.props, this.props.type), { label: this.props.minutesLabel, digit: this.state.minutesLeft, total: 60, type: "minutes" })) : null,
            this.props.showSeconds ? React.createElement(CounterComponent, __assign({}, select(this.props, this.props.type), { label: this.props.secondsLabel, digit: this.state.secondsLeft, total: 60, type: "seconds" })) : null);
    };
    Countdown.displayName = 'Countdown';
    Countdown.propTypes = {
        /** When the countdown should end.  */
        endDate: PropTypes.instanceOf(Date).isRequired,
        /** Flag indicating whether to show days value in the countdown. */
        showDays: PropTypes.bool,
        /** Label for the days digit. */
        daysLabel: PropTypes.string,
        /** Flag indicating whether to show hours value in the countdown. */
        showHours: PropTypes.bool,
        /** Label for the hours digit. */
        hoursLabel: PropTypes.string,
        /** Flag indicating whether to show minutes value in the countdown. */
        showMinutes: PropTypes.bool,
        /** Label for the minutes digit. */
        minutesLabel: PropTypes.string,
        /** Flag indicating whether to show seconds value in the countdown. */
        showSeconds: PropTypes.bool,
        /** Label for the seconds digit. */
        secondsLabel: PropTypes.string,
        /** The type of counter to display. */
        type: PropTypes.oneOf(['circle', 'flip', 'text']),
        /** Available Style Keys. */
        style: PropTypes.shape({
            /** Top level style applied to the ul element. */
            countdown: PropTypes.object,
            /** Style applied to CircleCounter component. */
            circle: PropTypes.object,
            /** Style applied to FlipCounter component. */
            flip: PropTypes.object,
            /** Style applied to TextCounter component. */
            text: PropTypes.object
        })
    };
    Countdown.defaultProps = {
        showDays: true,
        showHours: true,
        showMinutes: true,
        showSeconds: true,
        type: 'circle'
    };
    return Countdown;
}(React.Component));
export default Countdown;
